import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, watch } from "vue";
import { RemovableRef, useLocalStorage } from "@vueuse/core";

import type { AuthenticationResult, JsonWebToken } from "@/types/authentication";
import type { TenantContextPayload } from "@/types/api/fetch";
import { DateTime } from "luxon";
import { normalizeDateTime, utcNow } from "@/helpers/dateTimeUtilities";
import {
    buildStandardHeaders,
    defaultReasonForAbortingApiCall,
    goFetch,
    isOkStatus,
    isSuccessState,
    normalizeFetchParameters
} from "@/utils/network-utils";

import { clearToken, setToken } from "@/api/global";

import { AuthenticationFailed, NotFound } from "@/types/api/responses";
import { ApplicationId } from "@/constants";

import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

function parseJwtExpiration( jwt: string ): DateTime | null {
    try {
        // JWT structure: header.payload.signature
        const parts = jwt.split( '.' );
        if ( parts.length !== 3 ) {
            return null;
        }

        // Decode the payload (second part) from base64url
        const payload = JSON.parse( atob( parts[ 1 ].replace( /-/g, '+' ).replace( /_/g, '/' ) ) );

        // Extract exp claim (Unix timestamp in seconds)
        if ( payload?.exp ) {
            return DateTime.fromSeconds( payload.exp );
        }

        return null;
    } catch ( err ) {
        logger.error( `Failed to parse JWT expiration: ${ err }` );
        return null;
    }
}

function isValidJwt( jwt: string ): boolean {
    const expiration = parseJwtExpiration( jwt );
    if ( !expiration ) {
        return false;
    }

    return expiration > DateTime.utc();
}

function assignToken( token: string | JsonWebToken ): void {
    logger.debug( `[ auth-store ].assignToken( "${ token }" ) called` );

    let tempHolder: JsonWebToken | undefined;
    if ( typeof token === 'string' ) {
        // Parse the JWT to extract expiration
        const expiration = parseJwtExpiration( token );

        tempHolder = {
            jwt: token,
            sessionId: undefined,
            timeToLive: expiration
        };
    } else {
        // Store the value of the shared JWT token
        tempHolder = token;
    }

    lsToken.value = JSON.stringify( tempHolder );
}

function parseResponse( payload: AuthenticationResult): JsonWebToken | undefined {
    let result: JsonWebToken | undefined = undefined;
    const authResult = payload?.authResult;
    if ( authResult ) {
        const tokenValue = authResult.token ?? undefined;
        logger.debug( `Received token: ${ tokenValue }` );

        // track the session ID for the JsonWebToken
        const sessionId = authResult.sessionId ?? undefined;
        logger.debug( `Received session ID: ${ sessionId }` );

        // track the TTL for the JsonWebToken
        const ttl = normalizeDateTime( authResult.validTo ?? undefined );
        logger.debug( `Received ttl: ${ ttl }` );

        // create the JsonWebToken container from the data available from the Flagship authentication response
        result = {
            jwt: tokenValue,
            sessionId: sessionId,
            timeToLive: ttl
        };
    }

    return result;
}

function reset() {
    lsToken.value = null;
}

/**
 * Verify that the session id is still valid on the server
 * @returns - expected response is 200 (valid), 204 (not valid), 401 (unauthorized) or 403 (forbidden)
 */
export async function checkForValidSession( authToken: string | null ): Promise< boolean > {
    let isSessionStillValid = false;

    // If no JWT is provided, it is assumed the user is not logged in & thus the session is no longer valid
    if ( !authToken ) {
        return isSessionStillValid;
    }

    const fetchParams = normalizeFetchParameters( {
        endpoint: 'authentication/sessionsummarybyjwt',
        payload: authToken
    } );

    const headers = buildStandardHeaders( authToken );

    // Create an AbortController to short-circuit an API call that is taking too long
    const monitor = new AbortController();
    const timeoutId = setTimeout( () => monitor.abort( defaultReasonForAbortingApiCall ), fetchParams.maxApiDuration );

    try {
        const sessionResponse: Response =
            await fetch( fetchParams.uri,  {
                method: fetchParams.method,
                body: fetchParams.body,
                headers: headers,
                signal: monitor.signal
            } );

        // Clear the timeout at the earliest point to avoid false negative results
        clearTimeout( timeoutId );

        // Check if response is OK status (200-299)
        if ( isOkStatus( sessionResponse?.status ) ) {
            // Server confirmed the session is valid
            isSessionStillValid = true;
        } else {
            // Non-OK status means session is invalid
            // 401 = Unauthorized (session expired or invalid token)
            // 403 = Forbidden (token not allowed to access this endpoint - still means session is invalid)
            // 204 = No Content (session not found)
            logger.warn( `Session verification failed with status ${sessionResponse?.status} - session is invalid` );
            isSessionStillValid = false;
        }
    } catch ( err: any ) {
        // Clear the timeout at the earliest point to avoid false negative results
        clearTimeout( timeoutId );

        logger.debug( `Session verification failed: ${err.message}` );
        isSessionStillValid = false;
    }

    return isSessionStillValid;
}

const TOKEN_LOCALSTORAGE_KEY: string = 'token';

// Assigned in the "assignToken" action
const lsToken: RemovableRef< string | null > = useLocalStorage( TOKEN_LOCALSTORAGE_KEY, null );

const token = computed< JsonWebToken | null >( () => {
    logger.debug( `TokenManager.getToken(): this._token[ ${ JSON.stringify( lsToken.value ) } ], this._tokenKeyName[ ${ TOKEN_LOCALSTORAGE_KEY } ]` );
    const localToken = !!( lsToken.value ) ? JSON.parse( lsToken.value ) : null;

    if ( localToken ) {
        const ttlExists = !!( localToken!.timeToLive );
        if ( ttlExists ) {
            const ttlIsValid = normalizeDateTime( localToken!.timeToLive ).isValid;
            if ( ttlIsValid ) {
                const isStillAlive = utcNow() < normalizeDateTime( localToken!.timeToLive );
                if ( isStillAlive ) {
                    return localToken;
                }
            }
        }
    }

    // Token is invalid/expired - return null (reset handled in watch)
    return null;
} );

const authToken = computed< string | null >( () => token.value?.jwt ?? null );
// Track changes to the authentication token to automatically assign the Bearer token in any pinia-colada calls
watch( authToken, ( newValue, oldValue ) => {
    if ( newValue ) {
        setToken( newValue );
    } else {
        clearToken();
        // If we had a token, but now it's null (expired/invalid), clear localStorage
        if ( oldValue && !newValue && lsToken.value !== null ) {
            reset();
        }
    }
}, { immediate: true } );

export const useAuthStore = defineStore( 'authentication', () => {
    const isAuthenticated = computed< boolean >( () => !!( token.value?.jwt ) );

    async function authenticate( credentials: any ) {
        const username: string = credentials?.username ?? undefined;
        const password: string = credentials?.password ?? undefined;

        logger.debug( `[AuthHandler].authenticate( ${ username }, ${ password } )` );

        if ( !( username && password ) ) {
            throw new Error( `The username & password are required: ${ JSON.stringify( credentials ) }` );
        }

        const response =
            await goFetch< AuthenticationResult, TenantContextPayload >( {
                endpoint: `authentication/initiateauth`,
                payload: {
                    secrets: [
                        { key: "username", value: username },
                        { key: "userpassword", value: password }
                    ]
                }
            } ).catch( ( err: any ) => {
                throw new AuthenticationFailed( err.message );
            } );

        if ( response?.data && isSuccessState( response.status ) ) {
            const authToken: JsonWebToken | undefined = parseResponse( response.data );
            if ( !!( authToken ) ) {
                assignToken( authToken! );
            }
        } else {
            throw new NotFound();
        }
    }

    /**
     * Clean up any current session information held on the server in a consistent manner and then
     * remove all artifacts of being logged in from the current browser (localstorage)
     */
    async function logout() {
        // regardless of the above result, clear out the current token from localstorage to avoid odd "auth" issues
        reset();
    }

    return {
        // ACTIONS
        assignToken,
        authenticate,
	isValidJwt,
        logout,

        // STATE
        authToken,
        isAuthenticated
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useAuthStore, import.meta.hot ) );
}